import React, { useMemo, useState } from 'react';
import { Menu, Popover } from 'antd';
import { ItemType, SubMenuType } from 'antd/lib/menu/hooks/useItems';
import { BulkMenuItems, PolicyMenuItem } from './bulk-policy-update-items';
import { useStores } from '../../store';
import './bulk-update.scss';
import CustomIcon, { IconType } from '../custom-icon/custom-icon';
import { ActivationAction, BulkUpdateAction } from '../../../utils/bulkUpdateUtils';
import { observer } from 'mobx-react-lite';
import usePromiseFeedback from '../promise-feedback/usePromiseFeedback';
import { ActivationState, BulkProtectionOptions } from '../../../utils/enums';
import useCustomModal from '../custom-modal/custom-modal';
import { useActivation } from '../../../utils/activationUtil';
import activationService from '../../../services/activation.service';

interface PolicyUpdateProps {
    tab: BulkProtectionOptions;
}
export const BulkPolicyUpdate: React.FC<PolicyUpdateProps> = observer(({ tab }) => {
    const { fwVersionStore } = useStores();
    const { isPageDisabled, getPageType, getPageDisplayName } = useActivation();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [showModal, , contextHolder] = useCustomModal();

    const PageStatusMenuItem: PolicyMenuItem = {
        label: isPageDisabled[tab] ? ActivationAction.ACTIVATE : ActivationAction.DEACTIVATE,
        key: 'activate',
        icon: isPageDisabled[tab] ? IconType.PageActivationIcon : IconType.CLEAR,
        iconSize: 18,
        selectable: true,
        children: null
    };

    const menuItems: PolicyMenuItem[] = useMemo(() => {
        const menuItems = BulkMenuItems[tab] || [];
        if (tab === BulkProtectionOptions.SERVICES || tab === BulkProtectionOptions.IP_CONTROL) {
            return [...menuItems];
        }

        return [...menuItems, PageStatusMenuItem];
    }, [tab, isPageDisabled, PageStatusMenuItem]);

    const createMenu = (items: PolicyMenuItem[]): ItemType[] =>
        items?.map(item => {
            if (item.children) {
                return { ...getMenuItem(item), children: createMenu(item.children) } as SubMenuType;
            }
            return getMenuItem(item);
        });

    const getMenuItem = (item: PolicyMenuItem): ItemType => {
        const label = <div className={'custom-menu-item'}>
            {item.icon && <CustomIcon type={item.icon} size={item.iconSize}/>}
            {item.label}
        </div>;

        if (item.children) {
            return { key: item.key, label, onTitleClick: ({ domEvent }) => domEvent.stopPropagation() } as SubMenuType;
        }
        return { key: item.key, label, onClick: ({ domEvent }) => sendBulkUpdateOptionSelected(domEvent, item) };
    };

    const handleOpenChange = () => {
        setIsPopoverOpen(!isPopoverOpen);
    };

    const sendPageActivationOptionSelected = (item: PolicyMenuItem) => {
        const pageName = getPageDisplayName(tab);
        const pageType = getPageType(tab);

        if (item.label === ActivationAction.DEACTIVATE) {
            showModal({
                width: 640,
                title: `Deactivate ${pageName}`,
                body: () => <div className="modal-deactivation-commit-message">{
                    `You are about to deactivate ${pageName} Protection.\nThis will be added to the Draft version and will take effect once draft policy is applied.`}</div>,
                okText: 'Continue',
                cancelText: 'Cancel',
                onOk: () => promiseFeedbackFactory({
                    promise: fwVersionStore.genericCrudMethod(activationService.updatePageActivation, [{
                        pageType: pageType,
                        activationState: ActivationState.INACTIVE
                    }]),
                })
            });
        } else {
            promiseFeedbackFactory({
                promise: fwVersionStore.genericCrudMethod(activationService.updatePageActivation, [{
                    pageType: pageType,
                    activationState: ActivationState.ACTIVE
                }]),
            });
        }
    };

    const sendBulkUpdateOptionSelected = (event: any, item: PolicyMenuItem) => {
        if (item.children?.length) {
            event.stopPropagation();
            return;
        }

        setIsPopoverOpen(false);

        if (!item.updatedAction) {
            sendPageActivationOptionSelected(item);
        } else if (item.updatedAction === BulkUpdateAction.RECOMMENDATION) {
            promiseFeedbackFactory({
                promise: fwVersionStore.handleBulkUpdateRecommendationOption(item),
            });
        } else if (item.modalConfirmInfo) {
            const { text, title, okButtonText } = item.modalConfirmInfo;
            showModal({
                width: 400,
                title,
                body: () => <div className="modal-commit-message">{text}</div>,
                okText: okButtonText,
                cancelText: 'Cancel',
                onOk: () => promiseFeedbackFactory({
                    promise: fwVersionStore.handleBulkUpdateOptionSelected(item),
                })
            });
        } else {
            promiseFeedbackFactory({
                promise: fwVersionStore.handleBulkUpdateOptionSelected(item),
            });
        }
    };

    return (
        <>
            {contextHolder}
            <Popover
                placement="bottomRight"
                overlayClassName="popover-overlay-menu bulk-policy-menu"
                content={
                    <div className={'bulk-policy-menu-wrapper'}>
                        <Menu style={{ width: 260 }}
                            items={createMenu(menuItems)}
                            rootClassName={'bulk-policy-sub-menu'}
                            className={'popover-overlay-menu'}
                        />
                    </div>
                }
                open={isPopoverOpen}
                trigger="click"
                showArrow={false}
                onOpenChange={handleOpenChange}>
                <div className={`bulk-policy-wrapper ${fwVersionStore.isSelectedVersionEditable ? '' : 'disabled'}`}>
                    <div className="arrow-down-icon" />
                    {promiseContextHolder}
                </div>
            </Popover>
        </>
    );
});